import { DataTableConsumer } from '@/components/ui/data-table/types';
import { templatesTableConfig } from '@/config/table-configs';
import { FeedbackBadge } from '@/features/feedback/components/FeedbackBadge';
import {
  EntityListTable,
  type EntityListTableConfig,
} from '@/features/lists/components/EntityListTable';
import { templateTagKeys } from '@/features/lists/components/ListTagsField';
import { useCurrentUser } from '@/features/users/users.hooks';
import { ListSummary } from '@certhon/models';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { getTemplateActions } from './templates.actions';
import { templateFields } from '@/features/lists/lists.fields';

function useTemplateRowActions() {
  const currentUser = useCurrentUser();
  return useMemo(
    () => getTemplateActions(currentUser?.recnum ?? null),
    [currentUser],
  );
}

const extraColumns: ColumnDef<ListSummary>[] = [
  {
    id: 'feedback',
    header: 'Feedback',
    // Unresolved dominates ordering; resolved (processed) breaks ties.
    accessorFn: (list) => {
      const total = list.feedback_total_count ?? 0;
      const unresolved = list.feedback_unresolved_count ?? 0;
      const processed = total - unresolved;
      return unresolved * 1e4 + processed;
    },
    cell: ({ row }) => <FeedbackCountCell list={row.original} />,
  },
];

const config: EntityListTableConfig<ListSummary> = {
  tableId: 'templates',
  fields: templateFields,
  defaultColumnConfig: templatesTableConfig,
  tagKeys: templateTagKeys,
  statePrefix: 'tpl.',
  extraColumns,
  rowToHref: (list) => `/templates/${list.id}`,
  addPath: '/templates/new',
  useRowActions: useTemplateRowActions,
  entityName: 'templates',
};

export function TemplatesTable({
  data,
  isLoading,
}: DataTableConsumer<ListSummary>) {
  return (
    <EntityListTable<ListSummary>
      config={config}
      data={data}
      isLoading={isLoading}
    />
  );
}

// ---------------------------------------------------------------------------
// FeedbackCountCell — total count, color signals unresolved status.
// ---------------------------------------------------------------------------

function FeedbackCountCell({ list }: { list: ListSummary }) {
  const total = list.feedback_total_count;
  const unresolved = list.feedback_unresolved_count;

  // Counts may be undefined while data is still loading from a query that
  // didn't request aggregates. Render `-` rather than a misleading 0/0.
  if (total === undefined || unresolved === undefined || total === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return <FeedbackBadge unhandled={unresolved} total={total} size="small" />;
}
